import { useLogin, usePrivy, useWallets } from '@privy-io/react-auth';
import { useSetActiveWallet } from '@privy-io/wagmi';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { useShallow } from 'zustand/react/shallow';

import Loader from '@/components/ui/loader';
import config from '@/config';
import AccountSelect from '@/features/account/components/account-select';
import { getPrivyPrimaryAddress, usePrivyAccount } from '@/hooks';
import useFundedAccount from '@/hooks/use-funded-account';
import useRegisterUser from '@/lib/api/register-user';
import {
  clearPrimaryPrivateApiHeaders,
  clearPrivateApiHeaders,
  updatePrimaryPrivateApiHeaders,
  updatePrivateApiHeaders,
} from '@/lib/api-clients/rest-client';
import { cn, logConsole } from '@/lib/utils';
import { defaultChain } from '@/lib/wagmi/config';
import { useAccountStore } from '@/store/use-account-store';
import { useModalStore } from '@/store/use-modal-store';
import { EventType } from '@/types';
import { NetworkType, TransferType } from '@/types/enums';
import { sendEvent } from '@/utils/analytics';
import {
  handleCapitalStatusForStore,
  handleUserForStore,
} from '@/utils/handle-store-data';

import { Button } from '../ui';

import AccountModal from './modals/account-modal';
import GuideModal from './modals/guide-modal';
import RegisterDialog from './modals/register-dialog';
import SwitchModal from './modals/switch-modal';
import { TransferDialog } from './modals/transfer-dialog';
import WithdrawConfirmationModal from './modals/withdraw-confirmation-modal';

export default function ConnectButton() {
  const {
    address,
    hasAuth,
    hasPrimaryAuth,
    hasPrimaryAuthHydrated,
    hasAuthHydrated,
  } = useAccountStore(
    useShallow((state) => ({
      address: state.address,
      hasAuth: state.hasAuth,
      hasPrimaryAuthHydrated: state.hasPrimaryAuthHydrated,
      hasAuthHydrated: state.hasAuthHydrated,
      hasPrimaryAuth: state.hasPrimaryAuth,
    })),
  );

  const { t } = useTranslation();

  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isPrimaryRegisterModalOpen, setIsPrimaryRegisterModalOpen] =
    useState(false);

  const {
    authenticated,
    isConnected,
    isEmbedded,
    isPrimaryEmbedded,
    isPrimary,
    privyReady,
    networkType,
    address: primaryAddress,
    activeWallet: activePrivyWallet,
  } = usePrivyAccount();

  const authParams = useAccountStore(
    useShallow((state) => state.getAuthParams()),
  );
  const primaryAuthParams = useAccountStore(
    useShallow((state) => state.getAuthParams(primaryAddress)),
  );

  useEffect(() => {
    if (!address) return;
    logConsole(false, 'SWITCHED ACCOUNT: clearing orders and transfers');
    clearPrivateApiHeaders(['X-API-KEY']);
    const { setHasAuth } = useAccountStore.getState();
    if (!!authParams?.apiKey && address === authParams.address) {
      updatePrivateApiHeaders({
        'X-API-KEY': authParams.apiKey,
      });
      setHasAuth(true);
    } else {
      setHasAuth(false);
    }
  }, [address, authParams?.address, authParams?.apiKey]);

  useEffect(() => {
    const { setHasPrimaryAuth } = useAccountStore.getState();
    if (primaryAuthParams?.apiKey) {
      updatePrimaryPrivateApiHeaders({
        'X-API-KEY': primaryAuthParams.apiKey,
      });
      setHasPrimaryAuth(true);
    } else {
      clearPrimaryPrivateApiHeaders(['X-API-KEY']);
      setHasPrimaryAuth(false);
    }
  }, [primaryAuthParams?.address, primaryAuthParams?.apiKey]);

  const { isEval, isFundedOrEval, capitalStatusData } = useFundedAccount();

  useEffect(() => {
    if (!capitalStatusData) return;
    handleCapitalStatusForStore(capitalStatusData, address);
  }, [address, capitalStatusData]);

  const { mutate: registerUser } = useRegisterUser();

  useEffect(() => {
    if (!hasAuthHydrated || !address) return;
    if (isConnected && !hasAuth) {
      if (isEmbedded) {
        registerUser({ address, rememberMe: true });
      } else {
        setIsRegisterModalOpen(true);
      }
    } else if (isConnected && hasAuth) {
      setIsRegisterModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAuthHydrated, address, hasAuth, isConnected, isEmbedded]);

  useEffect(() => {
    if (!hasPrimaryAuthHydrated || !primaryAddress || !address || isPrimary)
      return;
    if (isConnected && !hasPrimaryAuth) {
      if (isPrimaryEmbedded) {
        registerUser({ address: primaryAddress, rememberMe: true });
      } else {
        setIsPrimaryRegisterModalOpen(true);
      }
    } else if (isConnected && hasPrimaryAuth) {
      setIsPrimaryRegisterModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address,
    hasPrimaryAuthHydrated,
    primaryAddress,
    isPrimary,
    isConnected,
    hasPrimaryAuth,
    isPrimaryEmbedded,
  ]);

  const { wallets } = useWallets();
  const { setActiveWallet } = useSetActiveWallet();

  const activeWallet = useMemo(
    () => wallets.find((wallet) => wallet.address === address),
    [wallets, address],
  );

  useEffect(() => {
    if (!activeWallet) return;
    if (networkType === NetworkType.EVM) {
      const evmWallet = activeWallet;
      if (evmWallet.walletClientType === 'privy') {
        const chainId = (evmWallet.chainId as string).split(':')[1];
        if (
          defaultChain.id.toString() !== chainId &&
          window.location.pathname !== '/custom-network-withdraw'
        ) {
          evmWallet.switchChain(defaultChain.id);
        }
      }
      setActiveWallet(evmWallet);
    }
    // For Solana, no chain switching required
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWallet, networkType]);

  const { address: evmAddress } = useAccount();

  const isWrongAddress =
    isConnected &&
    !!activePrivyWallet &&
    !isEmbedded &&
    !!address &&
    !!evmAddress &&
    networkType === NetworkType.EVM &&
    evmAddress !== address;

  useEffect(() => {
    useModalStore.setState({ isSwitchModalOpen: isWrongAddress });
  }, [isWrongAddress]);

  const { user } = usePrivy();

  useEffect(() => {
    // NOTE: fix for edge case where address is empty but user is authenticated
    if (!isConnected || !!address) return;
    const { setAddress } = useAccountStore.getState();
    const primaryAddress = getPrivyPrimaryAddress(user);
    if (!primaryAddress) return;
    setAddress(primaryAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, isConnected, user?.wallet?.address, user?.linkedAccounts]);

  const { login } = useLogin({
    onComplete: ({ user }) => handleUserForStore(user),
  });

  const onSignIn = () => {
    sendEvent(EventType.AUTH_FLOW_STARTED);
    login();
  };

  if (!privyReady) {
    return (
      <div className="flex items-center border-l border-border px-6 sm:px-8">
        <Loader className="w-11" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative z-40 flex h-full max-w-max gap-4 border-l border-border font-mono text-sm',
      )}
    >
      <div className="grid">
        {!isConnected || !address ? (
          <div className="flex w-full items-center justify-between gap-2 px-6 sm:px-7">
            <Button
              variant="contrast"
              size="xl"
              className="h-12 capitalize lg:px-12"
              disabled={authenticated}
              onClick={onSignIn}
            >
              {t('connectButton.signUp')}
            </Button>

            <Button
              variant="secondary"
              size="xl"
              className="h-12 capitalize lg:px-12"
              disabled={authenticated}
              onClick={onSignIn}
            >
              {t('connectButton.logIn')}
            </Button>
          </div>
        ) : (
          <div className="flex w-full justify-between gap-4 px-6 md:px-7">
            <AccountSelect />
            {!isEval && (
              <div className="hidden items-center gap-4 md:flex">
                <div className="flex h-10 items-center gap-2">
                  {!config.isDevelopment && isEmbedded ? (
                    isFundedOrEval ? (
                      <Button
                        variant="contrast"
                        size="xl"
                        className="h-12 capitalize"
                        asChild
                      >
                        <Link to="/vest-capital#capital-plans">
                          {t('fundingModal.buyMore')}
                        </Link>
                      </Button>
                    ) : (
                      <Button
                        variant="contrast"
                        size="xl"
                        className="h-12 capitalize"
                        asChild
                      >
                        <Link to="/deposit">{t('fundingModal.deposit')}</Link>
                      </Button>
                    )
                  ) : (
                    <TransferDialog orderType={TransferType.DEPOSIT} />
                  )}
                  <TransferDialog orderType={TransferType.WITHDRAW} />
                </div>
              </div>
            )}
            <RegisterDialog
              open={isRegisterModalOpen}
              setOpen={setIsRegisterModalOpen}
            />
            <RegisterDialog
              isForPrimary
              open={isPrimaryRegisterModalOpen}
              setOpen={setIsPrimaryRegisterModalOpen}
            />
          </div>
        )}
        <GuideModal />
        <SwitchModal />
        <WithdrawConfirmationModal />
        <AccountModal />
      </div>
    </div>
  );
}
